<script lang="ts" setup>
import { computed } from 'vue';
import { formatTimeAgo } from '@vueuse/core';
import type { Project } from '~/utilities/pyscript-api-models';
import { toProjectPage } from '~/utilities/to-project-page';
import { useUserStore } from '~/stores/user-store';
import { useProjectSettingsModalStore } from '~/stores/project-settings-modal-store';
import ProjectThreeDotMenu from '~/components/ProjectThreeDotMenu.vue';
import ProjectCatalogTags from '~/components/project-catalog/ProjectCatalogTags.vue';
import FavoriteButton from '~/components/buttons/FavoriteButton.vue';
import IconLock from '~icons/material-symbols/lock';
import IconExternalLink from '~icons/carbon/launch';

const props = defineProps<{
  project: Project;
  tagClickHandler: (...args: any) => void;
}>();

defineEmits<{
  (e: 'projectDeleted', value: Project): void;
  (e: 'projectUpdated', value: Project): void;
}>();

const userStore = useUserStore();
const projectSettingsModalStore = useProjectSettingsModalStore();

const isProjectOwner = computed(() => userStore.isProjectOwner(props.project));

function openUserProfileInNewTab(href: string) {
  if (!href) return;
  window.open(href, '_blank');
}
</script>

<template>
  <section
    class="relative rounded border border-new-gray-100/70 bg-white transition-shadow hover:border-new-gray-100 hover:shadow-lg dark:border-new-gray-950/80 dark:bg-gray-675"
  >
    <div
      class="flex h-full flex-col gap-4 rounded-[3px] border-l-[5px] px-5 py-5"
      :class="[project.type === 'app/invent' ? 'border-l-invent-blue' : 'border-l-new-gray-400']"
    >
      <div class="flex flex-nowrap items-start gap-2">
        <div class="flex w-full items-center gap-3 overflow-hidden">
          <img
            class="h-9 w-9 flex-shrink-0 rounded bg-new-slate-300 object-cover dark:bg-new-gray-800"
            :src="`${project.icon}?size=small`"
            :alt="project.name"
            width="36"
            height="36"
            loading="lazy"
          />

          <div class="w-full overflow-hidden">
            <div class="mb-1 flex gap-1.5">
              <router-link
                :to="toProjectPage(project)"
                class="truncate text-sm font-semibold leading-tight before:absolute before:inset-0 before:block"
              >
                {{ project.name }}
              </router-link>

              <IconLock
                v-if="project.auth_required"
                v-tooltip="{
                  content: 'Private Project',
                }"
                class="shrink-0 text-13 text-new-blue-400"
              />
            </div>

            <p class="truncate text-xs leading-tight text-black/60 dark:text-white/60">
              <span v-if="isProjectOwner">
                {{ $t('general.last_edited') }}
                {{ formatTimeAgo(new Date(project.updated_at)) }}
              </span>

              <button
                v-else
                class="relative hover:underline"
                @mouseup.middle="
                  openUserProfileInNewTab(
                    $router.resolve({
                      name: 'profile',
                      params: { usernameOrUserId: project.username || project.user_id },
                    }).href,
                  )
                "
                @click="
                  $router.push({
                    name: 'profile',
                    params: { usernameOrUserId: project.username || project.user_id },
                  })
                "
                >@{{ project.username || project.user_id }}</button
              >
            </p>
          </div>
        </div>

        <div class="-mr-2 -mt-1.5 flex items-center">
          <FavoriteButton :project="project" size="lg" class="relative" />

          <ProjectThreeDotMenu
            menu-btn-class-list="rounded-full text-new-gray-800 transition-colors hover:bg-new-gray-100/50 dark:text-new-gray-100 p-1"
            :project="project"
            :can-delete="isProjectOwner"
            version="latest"
            @project-deleted="$emit('projectDeleted', $event)"
            @project-updated="$emit('projectUpdated', $event)"
          />
        </div>
      </div>

      <div class="h-5 text-sm text-new-gray-700 dark:text-new-gray-200">
        <button
          v-if="!project.description && isProjectOwner"
          class="relative italic hover:underline"
          @click="projectSettingsModalStore.open(props.project, 0)"
        >
          Add a description...
        </button>
        <p v-else class="truncate">{{ project.description }}</p>
      </div>

      <div class="flex items-center gap-6">
        <ProjectCatalogTags
          class="pointer-events-none w-full"
          :project="project"
          :click-handler="tagClickHandler"
        />

        <a
          v-tooltip="{ content: $t('buttons.view_site') }"
          class="relative -m-1.5 rounded-full p-1.5 text-new-gray-800 transition-colors hover:bg-new-gray-100/50 dark:text-new-gray-100"
          :href="project.latest.url"
          target="_blank"
          rel="noopener noreferrer"
          :title="$t('buttons.view_site')"
        >
          <IconExternalLink class="text-sm" />
        </a>
      </div>
    </div>
  </section>
</template>
